/**
 * Signature-only rechecks for conversation rows whose hybrid signature failed
 * because the sender's ML-DSA key was not cached yet.
 *
 * Patches are computed from a snapshot and touch only signature fields. They
 * apply only to rows whose id and ciphertext still match and that are still
 * pending, so edits, deletes, and pages that land while a key fetch runs are
 * never overwritten.
 */

import {
  isSignatureCheckingActive,
  recheckPendingSignature,
  type DisplayMessageLike,
  type HealedSignature,
} from './messageDecryptionPipeline';

export interface SignatureRecheckSummary {
  healed: number;
  /** Key present, signature still invalid: final failure. */
  stillFailed: number;
  /** Sender ML-DSA key still unavailable. */
  noKey: number;
}

export const EMPTY_SIGNATURE_RECHECK_SUMMARY: SignatureRecheckSummary = {
  healed: 0,
  stillFailed: 0,
  noKey: 0,
};

type SignatureField = 'signatureVerified' | 'signatureVersion' | 'signaturePendingPqKey' | 'signatureCheckingUntil';

export interface SignatureRowPatch {
  ciphertext: string | undefined;
  set: Partial<Pick<DisplayMessageLike, SignatureField>>;
  clear: SignatureField[];
}

export function collectPendingSignatureRows<T extends DisplayMessageLike>(
  rows: Iterable<T>
): T[] {
  const out: T[] = [];
  for (const row of rows) {
    if (row.signaturePendingPqKey === true && !row.deleted) out.push(row);
  }
  return out;
}

/** Rows to show as "checking" while a key fetch for their sender runs. */
export function buildCheckingPatches(
  rows: readonly DisplayMessageLike[],
  senderIds: ReadonlySet<string>,
  until: number
): Map<string, SignatureRowPatch> {
  const patches = new Map<string, SignatureRowPatch>();
  for (const row of rows) {
    if (row.signaturePendingPqKey !== true || !senderIds.has(row.fromIdentityId)) continue;
    patches.set(row.id, { ciphertext: row.ciphertext, set: { signatureCheckingUntil: until }, clear: [] });
  }
  return patches;
}

/**
 * Recheck pending rows against the current key caches. Rows with no key yet
 * leave the checking state so they fall back to the unverified banner.
 */
export function buildRecheckPatches(args: {
  rows: readonly DisplayMessageLike[];
  signingKeys: Record<string, string>;
  pqSigningKeys: Record<string, string>;
  pinnedIdentityIds: readonly string[] | null;
  identityId: string;
  now?: number;
}): {
  patches: Map<string, SignatureRowPatch>;
  healed: HealedSignature[];
  summary: SignatureRecheckSummary;
} {
  const { rows, signingKeys, pqSigningKeys, pinnedIdentityIds, identityId } = args;
  const now = args.now ?? Date.now();
  const patches = new Map<string, SignatureRowPatch>();
  const healed: HealedSignature[] = [];
  const summary: SignatureRecheckSummary = { healed: 0, stillFailed: 0, noKey: 0 };

  for (const row of rows) {
    if (row.signaturePendingPqKey !== true) continue;
    const result = recheckPendingSignature(
      row,
      signingKeys[row.fromIdentityId],
      pqSigningKeys[row.fromIdentityId] || undefined,
      pinnedIdentityIds,
      identityId
    );
    if (!result) {
      summary.noKey += 1;
      patches.set(row.id, { ciphertext: row.ciphertext, set: {}, clear: ['signatureCheckingUntil'] });
      continue;
    }
    const { signatureVerified, signatureVersion } = result.row;
    patches.set(row.id, {
      ciphertext: row.ciphertext,
      set: signatureVersion != null ? { signatureVerified, signatureVersion } : { signatureVerified },
      clear:
        signatureVersion != null
          ? ['signaturePendingPqKey', 'signatureCheckingUntil']
          : ['signaturePendingPqKey', 'signatureCheckingUntil', 'signatureVersion'],
    });
    if (result.healed) {
      summary.healed += 1;
      healed.push({ message: result.row, wasVisiblyUnverified: !isSignatureCheckingActive(row, now) });
    } else {
      summary.stillFailed += 1;
    }
  }

  return { patches, healed, summary };
}

function patchApplies(row: DisplayMessageLike, patch: SignatureRowPatch): boolean {
  return row.ciphertext === patch.ciphertext && row.signaturePendingPqKey === true && !row.deleted;
}

/** Returns `row` itself when the patch would not change it. */
export function applySignatureRowPatch<T extends DisplayMessageLike>(row: T, patch: SignatureRowPatch): T {
  let changed = false;
  for (const [key, value] of Object.entries(patch.set) as [SignatureField, unknown][]) {
    if (row[key] !== value) changed = true;
  }
  for (const key of patch.clear) {
    if (key in row) changed = true;
  }
  if (!changed) return row;
  const next = { ...row, ...patch.set } as T;
  for (const key of patch.clear) delete next[key];
  return next;
}

/**
 * Apply patches to a row list. Returns the same array when nothing changed so
 * React state setters can bail out.
 */
export function applySignatureRowPatches<T extends DisplayMessageLike>(
  rows: readonly T[],
  patches: ReadonlyMap<string, SignatureRowPatch>
): readonly T[] {
  if (patches.size === 0) return rows;
  let changed = false;
  const next = rows.map((row) => {
    const patch = patches.get(row.id);
    if (!patch || !patchApplies(row, patch)) return row;
    const patched = applySignatureRowPatch(row, patch);
    if (patched !== row) changed = true;
    return patched;
  });
  return changed ? next : rows;
}

export function applySignatureRowPatchesToRecord<T extends DisplayMessageLike>(
  record: Readonly<Record<string, T>>,
  patches: ReadonlyMap<string, SignatureRowPatch>
): Readonly<Record<string, T>> {
  if (patches.size === 0) return record;
  let next: Record<string, T> | null = null;
  for (const [id, patch] of patches) {
    const row = record[id];
    if (!row || !patchApplies(row, patch)) continue;
    const patched = applySignatureRowPatch(row, patch);
    if (patched === row) continue;
    next ??= { ...record };
    next[id] = patched;
  }
  return next ?? record;
}
